import { Box, Slider, Typography } from '@mui/material';

/**
 * A slider in a tool's options: a 3 px rail on the divider colour, with a
 * taller hit area than the rail it draws.
 */
export const thinRailSliderSx = {
    py: 1,
    '& .MuiSlider-rail': {
        height: 3,
        opacity: 1,
        bgcolor: 'divider',
    },
} as const;

interface ToolTraySliderProps {
    /** Caption over the slider; omitted where the slider needs none. */
    label?: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    /**
     * The number read out to the right of the slider, formatted BY THE
     * CALLER: the tray is a UI-boundary control, so the conversion and
     * precision belong with the value's owner, not here. Omitted for a
     * slider whose setting has no meaningful number to show (see the
     * smoothing brush's strength).
     */
    valueText?: string;
    /** The unit after {@link valueText}, such as `cm` or `%`. */
    unit?: string;
    /**
     * Thumb offset for a slider whose travel starts below its usable
     * range (the brush's negative minimum), where the default centring
     * would let the thumb overhang the rail.
     */
    thumbMarginLeft?: string;
    'data-testid'?: string;
}

/**
 * One labelled slider in a contouring tool's options: a caption over the
 * slider and its read-out. It takes a row of its own in the options.
 */
export default function ToolTraySlider({
    label,
    value,
    onChange,
    min,
    max,
    step,
    valueText,
    unit,
    thumbMarginLeft,
    'data-testid': dataTestId,
}: ToolTraySliderProps) {
    return (
        <Box
            sx={{
                flex: '1 1 100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
            }}
        >
            {label !== undefined && (
                <Typography fontSize={11} color="text.secondary">
                    {label}
                </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Slider
                    value={value}
                    onChange={(_, newValue) =>
                        onChange(
                            Array.isArray(newValue) ? newValue[0] : newValue
                        )
                    }
                    step={step}
                    min={min}
                    max={max}
                    size="small"
                    aria-label={label}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        ...thinRailSliderSx,
                        ...(thumbMarginLeft !== undefined && {
                            '& .MuiSlider-thumb': {
                                marginLeft: thumbMarginLeft,
                            },
                        }),
                    }}
                    data-testid={dataTestId}
                />
                {valueText !== undefined && (
                    <Typography
                        component="span"
                        sx={(theme) => ({
                            flexShrink: 0,
                            minWidth: 40,
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                            fontFamily: theme.typography.fontFamilyMonospace,
                            fontSize: 12,
                            fontWeight: 500,
                        })}
                    >
                        {valueText}
                        {unit !== undefined && (
                            <Box
                                component="span"
                                sx={(theme) => ({
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: 11,
                                    fontWeight: 400,
                                    color: 'text.muted',
                                })}
                            >
                                {` ${unit}`}
                            </Box>
                        )}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
